/**
 * CSS File Selector Control
 * Handles CSS file upload with validation, minification, and preview
 *
 * Features:
 * - Single or multiple file selection
 * - File size validation (default 5MB per file)
 * - Format validation (.css, .min.css, .scss)
 * - File content preview
 * - Minification support
 * - CSS syntax validation ready
 */

import React, { useState, useCallback } from 'react';
import {
  validateFiles,
  sanitizeFilename,
  createLogger,
} from '@atlas-forms/client-js';
import type { ControlRendererProps } from '../ControlRenderer';

interface CssFileSelectorConfig {
  multiple?: boolean;
  acceptedFormats?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  validateOnChange?: boolean;
  showPreview?: boolean;
  minify?: boolean;
}

const CssFileSelectorControl: React.FC<ControlRendererProps> = ({
  control,
  value,
  onChange,
  disabled,
  mode,
}) => {
  const [preview, setPreview] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const config = (control.config as CssFileSelectorConfig) || {};
  const multiple = config.multiple ?? false;
  const maxFileSize = config.maxFileSize ?? 5 * 1024 * 1024; // 5MB
  const maxFiles = config.maxFiles ?? 1;
  const acceptedFormats = config.acceptedFormats ?? ['.css', '.min.css'];
  const showPreview = config.showPreview ?? true;
  const minify = config.minify ?? false;
  const validateOnChange = config.validateOnChange ?? true;

  const logger = createLogger('CssFileSelectorControl');

  // Validate file format
  const isValidFormat = (filename: string): boolean => {
    const name = filename.toLowerCase();
    return acceptedFormats.some(fmt => name.endsWith(fmt));
  };

  // Handle file selection
  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) {
        setError('');
        setPreview('');
        return;
      }

      try {
        setError('');
        setLoading(true);

        // Convert FileList to Array
        const fileArray = Array.from(files);

        // ← SECURITY: Validate file count and size
        const validation = validateFiles(fileArray);
        if (!validation.valid) {
          throw new Error(validation.error || 'File validation failed');
        }

        // ← SECURITY: Check formats
        const invalidFormats = fileArray.filter(f => !isValidFormat(f.name));
        if (invalidFormats.length > 0) {
          throw new Error(
            `Invalid format. Accepted: ${acceptedFormats.join(', ')}. ` +
            `Got: ${invalidFormats.map(f => f.name).join(', ')}`
          );
        }

        // ← SECURITY: Check individual file sizes
        const oversized = fileArray.filter(f => f.size > maxFileSize);
        if (oversized.length > 0) {
          throw new Error(
            `Files exceed ${maxFileSize / 1024 / 1024}MB limit: ${oversized.map(f => f.name).join(', ')}`
          );
        }

        // ← SECURITY: Check file count
        if (!multiple && fileArray.length > 1) {
          throw new Error('Multiple files not allowed for this field');
        }

        if (multiple && fileArray.length > maxFiles) {
          throw new Error(`Maximum ${maxFiles} files allowed`);
        }

        // Process selected files
        if (multiple) {
          // Process multiple files
          const processedFiles = await Promise.all(
            fileArray.map(async file => {
              try {
                const content = await file.text();
                const sanitized = sanitizeFilename(file.name);

                return {
                  name: sanitized,
                  size: file.size,
                  type: file.type,
                  content: minify ? minifyCss(content) : content,
                  originalName: file.name,
                  uploadedAt: new Date().toISOString(),
                };
              } catch (err) {
                logger.error(`Failed to process file ${file.name}:`, err);
                throw new Error(`Failed to process ${file.name}`);
              }
            })
          );

          onChange?.(processedFiles);

          // Show preview of first file
          if (showPreview && processedFiles.length > 0) {
            const preview = processedFiles[0].content.substring(0, 200);
            setPreview(preview + (preview.length > 200 ? '...' : ''));
          }
        } else {
          // Single file
          const file = fileArray[0];
          try {
            const content = await file.text();
            const sanitized = sanitizeFilename(file.name);

            const fileData = {
              name: sanitized,
              size: file.size,
              type: file.type,
              content: minify ? minifyCss(content) : content,
              originalName: file.name,
              uploadedAt: new Date().toISOString(),
            };

            onChange?.(fileData);

            // Show preview
            if (showPreview) {
              const previewText = content.substring(0, 200);
              setPreview(previewText + (previewText.length > 200 ? '...' : ''));
            }
          } catch (err) {
            logger.error(`Failed to process file ${file.name}:`, err);
            throw new Error(`Failed to process ${file.name}`);
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        onChange?.(null);
        logger.error('File selection failed:', err);
      } finally {
        setLoading(false);
      }
    },
    [multiple, maxFileSize, maxFiles, acceptedFormats, showPreview, minify, onChange, logger]
  );

  // Minify CSS (basic)
  const minifyCss = (code: string): string => {
    return code
      .replace(/\/\*[\s\S]*?\*\//g, '') // Remove comments
      .replace(/\s+/g, ' ') // Collapse whitespace
      .replace(/\s*([{}:;,>+~])\s*/g, '$1') // Remove spaces around selectors
      .trim();
  };

  // View mode
  if (mode === 'view') {
    const fileData = value as any;
    if (!fileData) {
      return (
        <div className="af-control__wrapper">
          {control.label && <label className="af-control__label">{control.label}</label>}
          <div className="af-control__empty">No file selected</div>
        </div>
      );
    }

    return (
      <div className="af-control__wrapper">
        {control.label && <label className="af-control__label">{control.label}</label>}
        <div className="af-css-selector__file-info">
          <div className="af-css-selector__file-name">🎨 {fileData.name || fileData.originalName}</div>
          <div className="af-css-selector__file-size">{(fileData.size / 1024).toFixed(2)} KB</div>
          {fileData.uploadedAt && (
            <div className="af-css-selector__upload-date">Uploaded: {fileData.uploadedAt}</div>
          )}
        </div>
        {showPreview && fileData.content && (
          <div className="af-css-selector__preview">
            <pre>{fileData.content.substring(0, 300)}</pre>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="af-control__wrapper">
      {control.label && <label className="af-control__label">{control.label}</label>}

      {error && (
        <div className="af-control__error-message" role="alert">
          ⚠️ {error}
        </div>
      )}

      <div className="af-css-selector__container">
        <label className="af-css-selector__drop-zone">
          <input
            type="file"
            multiple={multiple}
            accept={acceptedFormats.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
            disabled={disabled || loading}
            style={{ display: 'none' }}
          />
          <div className="af-css-selector__drop-zone-content">
            {loading ? (
              <span>Processing files...</span>
            ) : (
              <>
                <span className="af-css-selector__drop-zone-icon">🎨</span>
                <span className="af-css-selector__drop-zone-text">
                  Click to select or drag {acceptedFormats.join(', ')} file
                  {multiple ? 's' : ''}
                </span>
                <span className="af-css-selector__drop-zone-hint">
                  Max {maxFileSize / 1024 / 1024}MB per file
                </span>
              </>
            )}
          </div>
        </label>
      </div>

      {showPreview && preview && (
        <div className="af-css-selector__preview">
          <div className="af-css-selector__preview-title">Preview:</div>
          <pre>{preview}</pre>
        </div>
      )}

      {control.helpText && <div className="af-control__help">{control.helpText}</div>}
    </div>
  );
};

export default CssFileSelectorControl;
